import assert from 'assert';

import {FieldDefinitionType, TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {assertNever} from 'util/Types';

export interface FieldDefinitionForValidation {
  __typename: string;
  type: FieldDefinitionType;
  isMulti: boolean;
}

interface HasDropdown {
  dropdown: {__typename?: string; id: string} | {__typename: 'InaccessibleInfoNode'} | null;
}

interface TagSchemaNode {
  __typename: 'TagSchemaNode';
  id: string;
  itemType: TagSchemaItemType;
  folderItemType: string;
}
interface HasSchema {
  schema:
    | TagSchemaNode
    | {__typename: 'InaccessibleInfoNode'}
    | {__typename: Exclude<string, 'TagSchemaNode' | 'InaccessibleInfoNode'>; id: string}
    | null;
}

export type DropdownFieldDefinitionForValidation = FieldDefinitionForValidation & HasDropdown;
export type SchemaLinkFieldDefinitionForValidation = FieldDefinitionForValidation & HasSchema;

function tagSchemaItemTypeToFieldDefinitionType(itemType: TagSchemaItemType): FieldDefinitionType {
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return FieldDefinitionType.AA_SEQUENCE_LINK;
    case TagSchemaItemType.DNA_SEQUENCE:
    case TagSchemaItemType.DNA_OLIGO:
    case TagSchemaItemType.RNA_OLIGO:
    case TagSchemaItemType.RNA_SEQUENCE:
    case TagSchemaItemType.MIXED:
      return FieldDefinitionType.DNA_SEQUENCE_LINK;
    case TagSchemaItemType.ENTRY:
      return FieldDefinitionType.ENTRY_LINK;
    case TagSchemaItemType.MIXTURE:
      return FieldDefinitionType.MIXTURE_LINK;
    case TagSchemaItemType.MOLECULE:
      return FieldDefinitionType.MOLECULE_LINK;
    case TagSchemaItemType.BASIC_FOLDER_ITEM:
    case TagSchemaItemType.ANTIBODY:
    case TagSchemaItemType.EQUIPMENT:
    case TagSchemaItemType.OLIGO_CONJUGATE:
    case TagSchemaItemType.OLIGO_DUPLEX:
      return FieldDefinitionType.CUSTOM_ENTITY_LINK;
    default:
      assertNever(itemType);
      assert(false, `Unknown tag schema item type: ${itemType}`);
  }
}

// Exhaustive map: TypeScript will error if any FieldDefinitionType is missing.
export const FIELD_TYPE_SUBTYPES: Readonly<Record<FieldDefinitionType, ReadonlyArray<FieldDefinitionType>>> =
  {
    // Types with subtypes
    [FieldDefinitionType.ENTITY_LINK]: [
      FieldDefinitionType.AA_PART_LINK,
      FieldDefinitionType.AA_SEQUENCE_LINK,
      FieldDefinitionType.CUSTOM_ENTITY_LINK,
      FieldDefinitionType.DNA_SEQUENCE_LINK,
      FieldDefinitionType.MIXTURE_LINK,
      FieldDefinitionType.MOLECULE_LINK,
      FieldDefinitionType.PART_LINK,
      FieldDefinitionType.TRANSCRIPTION_LINK,
      FieldDefinitionType.TRANSLATION_LINK,
    ],
    // Types without subtypes
    [FieldDefinitionType.AA_PART_LINK]: [],
    [FieldDefinitionType.AA_SEQUENCE_LINK]: [],
    [FieldDefinitionType.ASSAY_REQUEST_LINK]: [],
    [FieldDefinitionType.ASSAY_RESULT_LINK]: [],
    [FieldDefinitionType.ASSAY_RUN_LINK]: [],
    [FieldDefinitionType.BATCH_LINK]: [],
    [FieldDefinitionType.BLOB_LINK]: [],
    [FieldDefinitionType.BOOLEAN]: [],
    [FieldDefinitionType.CUSTOM_ENTITY_LINK]: [],
    [FieldDefinitionType.DATE]: [],
    [FieldDefinitionType.DATETIME]: [],
    [FieldDefinitionType.DNA_SEQUENCE_LINK]: [],
    [FieldDefinitionType.DROPDOWN]: [],
    [FieldDefinitionType.ENTRY_LINK]: [],
    [FieldDefinitionType.FLOAT]: [],
    [FieldDefinitionType.INTEGER]: [],
    [FieldDefinitionType.JSON]: [],
    [FieldDefinitionType.LONG_TEXT]: [],
    [FieldDefinitionType.MIXTURE_LINK]: [],
    [FieldDefinitionType.MOLECULE_LINK]: [],
    [FieldDefinitionType.PART_LINK]: [],
    [FieldDefinitionType.SAMPLE_LINK]: [],
    [FieldDefinitionType.STORAGE_LINK]: [],
    [FieldDefinitionType.TEXT]: [],
    [FieldDefinitionType.TRANSCRIPTION_LINK]: [],
    [FieldDefinitionType.TRANSLATION_LINK]: [],
  };

export function areTypesCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): boolean {
  if (sourceField.__typename !== targetField.__typename) {
    return false;
  }

  if (isSchemaLinkFieldDefinition(targetField) && isSchemaLinkFieldDefinition(sourceField)) {
    const resolveTypeFromSchemaLink = (
      field: SchemaLinkFieldDefinitionForValidation
    ): FieldDefinitionType => {
      // For schema link field definitions we use ENTITY_LINK if it's "Any entity" or a specific schema link. Otherwise
      // we use the category of item as the type i.e. Any DNA Sequence is DNA_SEQUENCE_LINK. Therefore, when there is a
      // schema link we check the itemType of the schema to determine compatibility.
      const schema = field.schema;
      if (schema != null && !isInaccessible(schema) && isTagSchemaNode(schema)) {
        return tagSchemaItemTypeToFieldDefinitionType(schema.itemType);
      }
      return field.type;
    };

    const resolvedSourceType = resolveTypeFromSchemaLink(sourceField);
    const resolvedTargetType = resolveTypeFromSchemaLink(targetField);
    if (resolvedSourceType === resolvedTargetType) {
      return true;
    }
    return FIELD_TYPE_SUBTYPES[resolvedTargetType]?.includes(resolvedSourceType) ?? false;
  }

  return targetField.type === sourceField.type;
}

export function areCardinalitiesCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): boolean {
  return sourceField.isMulti === targetField.isMulti;
}

function isDropdownFieldDefinition(
  field: FieldDefinitionForValidation
): field is DropdownFieldDefinitionForValidation {
  return field.__typename === 'DropdownFieldDefinitionNode';
}

function isSchemaLinkFieldDefinition(
  field: FieldDefinitionForValidation
): field is SchemaLinkFieldDefinitionForValidation {
  return field.__typename === 'SchemaLinkFieldDefinitionNode';
}

function isTagSchemaNode(
  schema: NonNullable<SchemaLinkFieldDefinitionForValidation['schema']>
): schema is TagSchemaNode {
  return schema.__typename === 'TagSchemaNode';
}

function getDropdownId(field: DropdownFieldDefinitionForValidation): string | null {
  if (field.dropdown == null || isInaccessible(field.dropdown)) {
    return null;
  }
  return field.dropdown.id;
}

export function areDropdownsCompatible(
  sourceField: DropdownFieldDefinitionForValidation,
  targetField: DropdownFieldDefinitionForValidation
): boolean {
  return getDropdownId(sourceField) === getDropdownId(targetField);
}

function getSchemaId(field: SchemaLinkFieldDefinitionForValidation): string | null {
  if (field.schema == null || isInaccessible(field.schema)) {
    return null;
  }
  return field.schema.id;
}

export function areLinkSchemasCompatible(
  sourceField: SchemaLinkFieldDefinitionForValidation,
  targetField: SchemaLinkFieldDefinitionForValidation
): boolean {
  const targetSchemaId = getSchemaId(targetField);
  if (targetSchemaId == null) {
    return true;
  }
  return getSchemaId(sourceField) === targetSchemaId;
}

export interface FieldCompatibilityResult {
  isCompatible: boolean;
  incompatibilityReason: string | null;
}

export function areFieldDefinitionsCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): FieldCompatibilityResult {
  if (!areTypesCompatible(sourceField, targetField)) {
    return {
      isCompatible: false,
      incompatibilityReason: "Field definitions don't match: type mismatch.",
    };
  }

  if (!areCardinalitiesCompatible(sourceField, targetField)) {
    const sourceCardinality = sourceField.isMulti ? 'multiple values' : 'single value';
    const targetCardinality = targetField.isMulti ? 'multiple values' : 'single value';
    return {
      isCompatible: false,
      incompatibilityReason: `Field definitions don't match: source expects ${sourceCardinality}, target expects ${targetCardinality}.`,
    };
  }

  if (isDropdownFieldDefinition(sourceField) && isDropdownFieldDefinition(targetField)) {
    if (!areDropdownsCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: dropdown selector mismatch.",
      };
    }
  }

  if (isSchemaLinkFieldDefinition(sourceField) && isSchemaLinkFieldDefinition(targetField)) {
    if (!areLinkSchemasCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: schema mismatch.",
      };
    }
  }

  return {isCompatible: true, incompatibilityReason: null};
}
